import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { Role } from '../api/types';
import { HOME_BY_ROLE, useAuth } from './AuthProvider';
import { SkeletonCard } from '../ui/Skeleton';

export interface RequireRoleProps {
  roles: readonly Role[];
  children: ReactNode;
}

/**
 * Route guard. Čita korisnika iz `AuthProvider`, nikad iz URL-a ili localStoragea.
 * Autorizacija na razini rute je samo prvi sloj — API filtrira po `client_id` iz tokena
 * (ARHITEKTURA.md sekcija 4).
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="mx-auto w-full max-w-content p-6" aria-busy="true">
        <SkeletonCard />
      </div>
    );
  }

  if (status === 'anonymous' || !user) {
    return <Navigate to="/prijava" replace state={{ from: location.pathname }} />;
  }

  if (!roles.includes(user.role)) {
    return <Navigate to={HOME_BY_ROLE[user.role]} replace />;
  }

  // Klijent bez završenog onboardinga ide na upitnik prije svega ostalog.
  if (user.role === 'client' && !user.onboardingCompleted && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/** Prijavljeni korisnik nema što raditi na ekranima prijave i registracije. */
export function RequireAnonymous({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  if (status === 'loading') {
    return (
      <div className="mx-auto w-full max-w-narrow p-6" aria-busy="true">
        <SkeletonCard />
      </div>
    );
  }
  if (status === 'authenticated' && user) {
    return <Navigate to={HOME_BY_ROLE[user.role]} replace />;
  }
  return <>{children}</>;
}
